import { useEffect, useState } from 'react';
import { Loader2, Sparkles, Trash2, MessageCircle } from 'lucide-react';
import { digestsApi, type OrgDigest } from '../api/digests';
import { fmtTime } from '../lib/format';
import { Markdown } from '../lib/markdown';

interface Props { orgId: string; }

export default function OrgDigestsTab({ orgId }: Props) {
  const [items, setItems] = useState<OrgDigest[]>([]);
  const [stats, setStats] = useState<{ total_messages: number; last_message_at: string | null } | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const [list, st] = await Promise.all([
        digestsApi.list(orgId),
        digestsApi.chatStats(orgId).catch(() => ({ total_messages: 0, last_message_at: null })),
      ]);
      setItems(list);
      setStats(st);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [orgId]);

  const generate = async () => {
    setGenerating(true); setErr(null);
    try { await digestsApi.create(orgId); await load(); }
    catch (e: any) { setErr(e?.response?.data?.detail || e?.message || 'Ошибка'); }
    finally { setGenerating(false); }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Удалить дайджест?')) return;
    try { await digestsApi.remove(orgId, id); await load(); }
    catch (e: any) { setErr(e?.response?.data?.detail || e?.message || 'Ошибка'); }
  };

  return (
    <section className="bg-slate-900 border border-slate-800 rounded-xl">
      <header className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
        <h2 className="font-bold text-white flex items-center gap-2">
          <Sparkles size={16} className="text-purple-400" /> Дайджесты переписки
        </h2>
        <button onClick={generate} disabled={generating}
          className="text-xs flex items-center gap-1 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white px-3 py-1.5 rounded">
          {generating ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
          Сформировать за 7 дней
        </button>
      </header>

      <div className="p-4 space-y-4">
        {stats && (
          <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
            <MessageCircle size={11} />
            архив сообщений MAX-чата: <b className="text-slate-300">{stats.total_messages}</b>
            {stats.last_message_at && (
              <span> · последнее: <span className="text-slate-300">{fmtTime(stats.last_message_at)}</span></span>
            )}
          </div>
        )}

        {err && (
          <div className="bg-red-900/30 border border-red-800 text-red-300 px-3 py-2 rounded text-sm">{err}</div>
        )}

        {loading ? (
          <Loader2 className="animate-spin text-blue-400 mx-auto my-4" />
        ) : items.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-6">
            Дайджестов пока нет. Жми «Сформировать», когда в чате накопится переписка.
            <br />
            <span className="text-xs">Автоматический cron — каждый понедельник в 09:00 MSK.</span>
          </div>
        ) : (
          <ul className="space-y-3">
            {items.map(d => (
              <li key={d.id} className="bg-slate-950/50 border border-slate-800 rounded-lg p-4">
                <header className="flex items-start justify-between mb-2">
                  <div>
                    <div className="text-sm text-white font-medium">
                      {fmt(d.period_start)} — {fmt(d.period_end)}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {d.messages_count} сообщений · сформирован {fmtTime(d.generated_at)}
                      {d.generated_by_name && ` · ${d.generated_by_name}`}
                      {d.auto && ' · авто'}
                    </div>
                  </div>
                  <button onClick={() => remove(d.id)}
                    className="text-slate-500 hover:text-red-400 p-1" title="Удалить">
                    <Trash2 size={12} />
                  </button>
                </header>
                <article className="text-sm text-slate-200 leading-relaxed prose-digest">
                  <Markdown source={d.summary_md} />
                </article>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function fmt(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' });
}
